import CardContainer from '../components/cardContainer';
import Card from '../components/card';
import {
    useContext, useEffect, useMemo, useRef, useState 
} from 'react';
import NumberInputScreen from '../components/numberInput';
import {
    makeStyles 
} from '@mui/styles';
import {
    put 
} from '../../../networking';
import {
    Box, Slide, Typography, useTheme 
} from '@mui/material';
import ProgressBar from '../components/progressBar';
import {
    DiagnosticFormContext 
} from '../../../contexts/form';
import {
    TypographyVariants, TypographyWeights 
} from '../../../figmaTheme';

type Props = {
    position: number
};

const useStyles = makeStyles((theme) => ({
    scrollableContainer: {
        overflowY: 'scroll',
        overflowX: 'hidden',
        scrollbarWidth: 'none',
        minHeight: '100%',
        width: '100vw',
        maxWidth: '692px',
        transform: 'translate(-24px, 0)',
        padding: '0 24px'
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
    },
    title: {
        position: 'relative',
        margin: '0 0 10px'
    },
    subtitle: {
        margin: '0 0 30px'
    },
    error: {
        textAlign: 'center',
        color: theme.palette.error.main,
        margin: '8px 0 0'
    },
    fadeIn: {
        opacity: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        animation: '$fadeIn 0.5s forwards 0.5s',
    },
    '@keyframes fadeIn': {
        '0%': {
            opacity: 0 
        },
        '100%': {
            opacity: 1 
        }
    }
}));

export const DrinkCost = ({
    position
}: Props): JSX.Element => {
    const formContext = useContext(DiagnosticFormContext);
    const drinkCostAnswer = useMemo(() => (
        formContext.answers?.drinkCost?.toString()
    ), [formContext.answers?.drinkCost]);
    const classes = useStyles();
    const theme = useTheme();
    const [value, setValue] = useState(drinkCostAnswer || '');
    const [error, setError] = useState('');

    const inputRef = useRef(null);

    const onChange = (e) => {
        if (!e.target.value) {
            setValue('');
            setError('');
            return;
        }

        if (e.target.value <= 0) setError('Your drink spend must be more than $0');
        else if (parseInt(e.target.value).toString() !== e.target.value) setError('Please round to nearest whole dollar');
        else setError('');

        setValue(e.target.value);
    };

    const onKeyPress = (e) => {
        if (!value || error) return;

        if (e.key === 'Enter') onClick();
    };

    const onClick = () => {
        if (!value || error) return;
        formContext.setAnswers({
            drinkCost: parseFloat(value) 
        });

        const payload = {
            averageDrinkCalories: 125,
            averageDrinkCost: parseFloat(value),
            hideCalories: false
        };
        put('tracking-progress/user/preferences', payload);

        if (inputRef.current) inputRef.current.blur();

        setTimeout(() => {
            formContext.goNext();
        }, 50);
    };

    useEffect(() => {
        if (!inputRef.current) return;

        inputRef.current.scrollTo({
            top: 0,
            behaviour: 'smooth' 
        });
        const timeout = setTimeout(() => {
            inputRef.current.focus();
        }, 1000);

        return () => clearTimeout(timeout);
    }, [inputRef.current]);

    return (
        <CardContainer
            sectionKey={'habits'}
            goBack={formContext.goBack}
            hasGradient
        >
            <Card>
                <ProgressBar
                    currentPosition={position}
                    sectionKey={'habits'}
                />
                <Box className={classes.scrollableContainer}>
                    <Slide
                        in={true}
                        direction={'left'}
                        timeout={250}
                        mountOnEnter
                    >
                        <Box className={classes.content}>
                            <Typography
                                variant={TypographyVariants.H3}
                                className={classes.title}
                                dangerouslySetInnerHTML={{
                                    __html: `On average, how much would you estimate you <span style="font-weight:${TypographyWeights.BOLD};color:${theme.palette.secondary.main}">spend per drink?</span>` 
                                }}
                            />
                            <Typography
                                variant={TypographyVariants.H6}
                                className={classes.subtitle}
                                dangerouslySetInnerHTML={{
                                    __html: `We'll use this to estimate your personalized savings as you start to cut back. If you aren't sure, we recommend <span style="font-weight:${TypographyWeights.BOLD};color:${theme.palette.secondary.main}">$8</span> as a starting point.` 
                                }}
                            />
                        </Box>
                    </Slide>
                    <Box className={classes.fadeIn}>
                        <NumberInputScreen
                            sectionKey={'habits'}
                            inputRef={inputRef}
                            onChange={onChange}
                            onKeyPress={onKeyPress}
                            value={value}
                            unit={'$'}
                            showButton
                            onClick={onClick}
                            error={error}
                        />
                    </Box>
                    <Typography
                        variant={TypographyVariants.H6}
                        className={classes.error}
                    >
                        {error}
                    </Typography>
                    <Box
                        style={{
                            height: '300px' 
                        }}
                    />
                </Box>
            </Card>
        </CardContainer>
    );
};

export default DrinkCost;
